import { Controller } from "@hotwired/stimulus"

/**
 * Reading analytics for travel guides, stories and comparisons (PageReadRecorder on the server)
 * - A page view counts once the page has been on screen for 2 s and the visitor has done something on
 *   it (moved the mouse, touched the screen, turned the wheel, pressed a key): a reader who looks, moves
 *   or touches and goes Back is a bounce; a tab opened in the background and closed unseen is no visit,
 *   and neither is a headless browser that opens the page, waits and leaves (most of the beacons in the
 *   first days, November 2026). Nothing is sent before that. The price: a phone reader who leaves by the
 *   system Back gesture without ever touching the page, and a screen reader user, aren't counted
 * - What was read: each step on its own - a guide's days and its FAQ, or the four quarters of an
 *   article's text - once it has stayed on the reading line (40% down the screen) for consecutive
 *   samples a second apart: 2, or a second per screen of a tall step, so a fling that passes a long
 *   day at more than a screen a second doesn't read it. A step short enough to sit wholly in the
 *   middle of the screen counts there too, and the last step when the page is scrolled to its bottom
 *   with the step's end in the middle of the screen.
 *   A jump from the section nav, a deep link or a fling to the footer passes over the steps between
 * - Engaged time: only while the tab is visible and the reader did something in the last 30 s
 * - One small beacon when the page is hidden or left (Turbo visits included); a reader who comes back
 *   and reads on sends only what changed. No cookies, no ids, nothing about the visitor
 * - Nothing is sent from an automated browser (navigator.webdriver)
 *
 * Usage: data-controller="read-tracker" on the page wrapper, with
 *          data-read-tracker-kind-value="trip|blog|compare" and data-read-tracker-slug-value
 *        Guides: data-read-tracker-target="step" on each day section, in order, then on the FAQ
 *        Articles: data-read-tracker-target="start" and "end" markers around the text
 */
const ENDPOINT = "/api/v1/page_reads"
const VIEW_MS = 2000
const IDLE_MS = 30_000
// PageRead::MAX_ENGAGED_SECONDS
const MAX_SECONDS = 1800
// PageRead::DEPTHS.size
const QUARTERS = 4
// Where on the screen a reader reads, and the middle part they look at, as shares of its height
const READING_LINE = 0.4
const BAND_TOP = 0.15
const BAND_BOTTOM = 0.85
const SAMPLE_MS = 1000
const DWELL_SAMPLES = 2
// Within this many pixels of the bottom of the page counts as scrolled to the bottom
const BOTTOM_SLACK = 4
const INPUT_EVENTS = ["pointerdown", "pointermove", "keydown", "wheel", "touchstart", "scroll"]
// Input only a person gives. A scroll event is trusted even when a script or the browser scrolls (Turbo,
// a #day-3 link), so it keeps the reader engaged but doesn't make the visit a view
const PERSON_EVENTS = new Set(["pointerdown", "pointermove", "keydown", "wheel", "touchstart"])

export default class extends Controller<HTMLElement> {
  static targets = ["step", "start", "end"]
  static values = { kind: String, slug: String }

  declare readonly stepTargets: HTMLElement[]
  declare readonly startTarget: HTMLElement
  declare readonly hasStartTarget: boolean
  declare readonly endTarget: HTMLElement
  declare readonly hasEndTarget: boolean
  declare kindValue: string
  declare slugValue: string

  private tracking = false
  private sent = false
  private interacted = false
  private visible = false
  private visibleMs = 0
  private visibleSince = 0
  private engagedMs = 0
  private lastActivity = 0
  private dwell: number[] = []
  private seen = new Set<number>()
  private reported = new Set<number>()
  private reportedSeconds = 0
  private timer: number | null = null

  connect() {
    // Automated browsers (headless crawlers, test runners) say so. A Turbo preview (the cached copy
    // shown while the page loads) is replaced in a moment and would count the visit twice
    if (navigator.webdriver || typeof navigator.sendBeacon !== "function") return
    if (document.documentElement.hasAttribute("data-turbo-preview")) return

    this.tracking = true
    this.sent = false
    this.interacted = false
    this.visible = false
    this.visibleMs = 0
    this.engagedMs = 0
    this.dwell = new Array(this.totalSteps).fill(0)
    this.seen = new Set()
    this.reported = new Set()
    this.reportedSeconds = 0
    this.setVisible(document.visibilityState === "visible")

    INPUT_EVENTS.forEach((type) => window.addEventListener(type, this.onInput, { passive: true }))
    document.addEventListener("visibilitychange", this.onVisibilityChange)
    window.addEventListener("pagehide", this.onPageHide)
    window.addEventListener("pageshow", this.onPageShow)
  }

  disconnect() {
    if (!this.tracking) return

    // Leaving through a Turbo visit: no pagehide, the page just goes (already detached here, so
    // sample() doesn't measure it)
    this.flush()
    this.tracking = false
    this.stopSampling()
    INPUT_EVENTS.forEach((type) => window.removeEventListener(type, this.onInput))
    document.removeEventListener("visibilitychange", this.onVisibilityChange)
    window.removeEventListener("pagehide", this.onPageHide)
    window.removeEventListener("pageshow", this.onPageShow)
  }

  private get totalSteps() {
    return this.stepTargets.length || QUARTERS
  }

  private onInput = (event: Event) => {
    if (!event.isTrusted) return

    if (PERSON_EVENTS.has(event.type)) this.interacted = true
    this.addEngagedTime()
  }

  private onVisibilityChange = () => {
    this.setVisible(document.visibilityState === "visible")
    // The last moment a phone reliably lets a page send anything
    if (!this.visible) this.flush()
  }

  // Some browsers send pagehide into the back/forward cache without a hidden visibilitychange
  private onPageHide = () => {
    this.setVisible(false)
    this.flush()
  }

  // Back from the back/forward cache: the same visit goes on, from now
  private onPageShow = (event: PageTransitionEvent) => {
    if (event.persisted) this.setVisible(document.visibilityState === "visible")
  }

  // Time on screen, engaged time and sampling run only while the tab is visible; coming back to it
  // counts as activity, the time away doesn't
  private setVisible(visible: boolean) {
    if (visible === this.visible) return

    const now = performance.now()
    if (visible) {
      this.visibleSince = now
      this.lastActivity = now
      this.visible = true
      this.startSampling()
    } else {
      this.addEngagedTime()
      this.visibleMs += now - this.visibleSince
      this.visible = false
      this.stopSampling()
    }
  }

  private visibleTime() {
    return this.visibleMs + (this.visible ? performance.now() - this.visibleSince : 0)
  }

  // The reader counts as reading up to IDLE_MS after their last input, while the tab is visible
  private addEngagedTime() {
    const now = performance.now()
    if (this.visible) this.engagedMs += Math.min(now - this.lastActivity, IDLE_MS)
    this.lastActivity = now
  }

  private startSampling() {
    if (this.timer === null && this.seen.size < this.dwell.length) {
      this.timer = window.setInterval(this.sample, SAMPLE_MS)
    }
  }

  private stopSampling() {
    if (this.timer !== null) window.clearInterval(this.timer)
    this.timer = null
  }

  // A step being read now gets a sample, one out of view loses its count: the samples must be in a row
  private sample = () => {
    if (!this.element.isConnected) return

    const screen = window.innerHeight
    const line = screen * READING_LINE
    const atBottom = window.scrollY + screen >= document.documentElement.scrollHeight - BOTTOM_SLACK
    const ranges = this.stepRanges()
    ranges.forEach(([top, bottom], index) => {
      if (this.seen.has(index)) return

      const onLine = top <= line && bottom >= line
      const wholeInMiddle = top >= screen * BAND_TOP && bottom <= screen * BAND_BOTTOM
      // The end of the text in the middle of the screen, not a sliver above a tall footer
      const lastAtBottom = index === ranges.length - 1 && atBottom && bottom >= screen * BAND_TOP && bottom <= screen * BAND_BOTTOM
      if (!onLine && !wholeInMiddle && !lastAtBottom) {
        this.dwell[index] = 0
        return
      }

      this.dwell[index] += 1
      if (this.dwell[index] >= Math.max(DWELL_SAMPLES, Math.ceil((bottom - top) / screen))) this.seen.add(index)
    })
    if (this.seen.size >= this.dwell.length) this.stopSampling()
  }

  // [top, bottom] of each step on screen: the step elements, or the article's text in quarters
  private stepRanges(): [number, number][] {
    if (this.stepTargets.length > 0) {
      return this.stepTargets.map((step) => {
        const rect = step.getBoundingClientRect()
        return [rect.top, rect.bottom]
      })
    }
    if (!this.hasStartTarget || !this.hasEndTarget) return []

    const top = this.startTarget.getBoundingClientRect().top
    const quarter = (this.endTarget.getBoundingClientRect().top - top) / QUARTERS
    if (quarter <= 0) return []

    return Array.from({ length: QUARTERS }, (_, index) => [top + index * quarter, top + (index + 1) * quarter])
  }

  private flush() {
    if (!this.tracking) return

    this.addEngagedTime()
    if (!this.sent && (!this.interacted || this.visibleTime() < VIEW_MS)) return

    const seconds = Math.min(Math.floor(this.engagedMs / 1000), MAX_SECONDS)
    const seen = [...this.seen].filter((index) => !this.reported.has(index)).sort((a, b) => a - b)
    if (this.sent && seconds === this.reportedSeconds && seen.length === 0) return

    const beacon = {
      kind: this.kindValue,
      slug: this.slugValue,
      total: this.totalSteps,
      first: !this.sent,
      seconds,
      reported_seconds: this.reportedSeconds,
      seen,
    }
    const queued = navigator.sendBeacon(ENDPOINT, new Blob([JSON.stringify(beacon)], { type: "application/json" }))
    // Not queued (the browser's beacon budget is full): the next flush carries it all
    if (!queued) return

    this.sent = true
    this.reportedSeconds = seconds
    seen.forEach((index) => this.reported.add(index))
  }
}
